/**
 * React Query keys for the Locations module, and the one place that invalidates them.
 *
 * Everything lives under the 'locations' prefix. Other modules read the shared keys
 * ['locations', 'all'] (getAll), ['locations', 'active'] (getAllActive), ['locations', 'list-view']
 * (getAllViews) and the bare ['locations'] (getAll), so the data shapes here MUST match theirs.
 */

import type { QueryClient } from '@tanstack/react-query';

export interface CampusMembersParams {
  page: number;
  pageSize: number;
  search: string;
  includeServe: boolean;
  isActive: boolean | null;
}

export const locationsKeys = {
  root: ['locations'] as const,
  /** Location[] from getAll() (not archived). Shared with Announcements, Bookings… */
  all: ['locations', 'all'] as const,
  /** Location[] from getAllActive(). Shared with Budgets, Procurement, Vendors… */
  active: ['locations', 'active'] as const,
  /** LocationModelView[] from getAllViews(). Shared with Meetings, Projects, Tasks. */
  listView: ['locations', 'list-view'] as const,
  archived: ['locations', 'archived'] as const,
  detail: (id: number) => ['locations', 'detail', id] as const,
  view: (id: number) => ['locations', 'view', id] as const,
  usage: (id: number) => ['locations', 'usage', id] as const,
  address: (id: number) => ['locations', 'address', id] as const,
  serviceTimes: (id: number) => ['locations', 'service-times', id] as const,
  allServiceTimes: ['locations', 'service-times', 'all'] as const,
  /** Archived service times of one location, or of every location (null). LocationID 0 is valid. */
  archivedServiceTimes: (id: number | null) => ['locations', 'service-times', 'archived', id ?? 'all'] as const,
  venues: (id: number) => ['locations', 'venues', id] as const,
  allVenues: ['locations', 'venues', 'all'] as const,
  /** Archived venues of one location, or of every location (null). LocationID 0 is valid. */
  archivedVenues: (id: number | null) => ['locations', 'venues', 'archived', id ?? 'all'] as const,
  leaders: (id: number) => ['locations', 'leaders', id] as const,
  allLeaders: ['locations', 'leaders', 'all'] as const,
  /** LocationLeaderView[] (leaders with member names, resolved server-side). */
  leaderViews: (id: number) => ['locations', 'leaders', id, 'view'] as const,
  allLeaderViews: ['locations', 'leaders', 'all', 'view'] as const,
  info: (id: number) => ['locations', 'info', id] as const,
  socialPublic: (id: number) => ['locations', 'social', id, 'public'] as const,
  socialFull: (id: number) => ['locations', 'social', id, 'full'] as const,
  platforms: ['locations', 'platforms'] as const,
  members: (id: number, params: CampusMembersParams) => ['locations', 'members', id, params] as const,
};

/** Member search for the "Add a leader" picker (not under 'members': another module owns that prefix). */
export const memberSearchKey = (term: string) => ['location-member-search', term] as const;

/**
 * Call after every Locations write. Invalidates the shared keys other modules use, this module's
 * keys (all under the 'locations' prefix) and the neighbours that mirror Locations data:
 * Meetings venue pickers / admin venues and Carpool campuses.
 */
export function invalidateLocationQueries(queryClient: QueryClient, options: { venues?: boolean } = {}) {
  queryClient.invalidateQueries({ queryKey: ['locations', 'all'] });
  queryClient.invalidateQueries({ queryKey: ['locations', 'active'] });
  queryClient.invalidateQueries({ queryKey: ['locations', 'list-view'] });
  // Prefix match: also the bare ['locations'] key (TaskManagement / Vendors) and every key above.
  queryClient.invalidateQueries({ queryKey: ['locations'] });
  // Meetings (LocationVenuePicker / admin venues) keeps its own location + venue keys.
  queryClient.invalidateQueries({ queryKey: ['locations-active'] });
  if (options.venues) queryClient.invalidateQueries({ queryKey: ['location-venues'] });
  // Carpool campuses follow Locations (address, service times, active flag).
  queryClient.invalidateQueries({ queryKey: ['rides', 'campuses'] });
}
